import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { ASSETS, LINKS } from "../lib/constants";
import StoreButtons from "./StoreButtons";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative pt-28 lg:pt-36 pb-20 lg:pb-28 overflow-hidden noise"
        >
            {/* backdrops */}
            <div className="absolute inset-0 grid-backdrop opacity-60" />
            <div className="absolute inset-0 radial-glow" />
            <div
                className="absolute top-24 left-8 lg:left-20 formula-bg wave-float"
                aria-hidden
            >
                {
                    "C(t) = (F·D·kₐ / V·(kₐ−kₑ)) · (e^−kₑt − e^−kₐt)\n\nt_peak = ln(kₐ/kₑ) / (kₐ − kₑ)\n\nC_ss = F·D / V·kₑ·τ\n\nA:E = Anabolic : Estrogen\n\nT_free = Vermeulen(T_total, SHBG, Alb)"
                }
            </div>

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
                {/* Left column */}
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="eyebrow mb-6"
                    >
                        <Star
                            size={11}
                            strokeWidth={0}
                            fill="currentColor"
                            className="text-[#49bef5] drop-shadow-[0_0_6px_rgba(73,190,245,0.8)]"
                        />{" "}
                        Charted #2 · Health &amp; Fitness · App Store
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.05 }}
                        className="font-brand text-6xl sm:text-7xl lg:text-8xl leading-none mb-7 select-none"
                        data-testid="hero-wordmark"
                    >
                        <span className="text-white">TRT</span>
                        <span className="text-[#2ba6e0]">+</span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.12 }}
                        className="font-display font-bold clamp-hero text-white"
                        data-testid="hero-headline"
                    >
                        Hormone optimization.
                        <br />
                        <span className="blue-glow">Understood.</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mt-6 max-w-xl text-base lg:text-lg text-white/70 leading-relaxed"
                        data-testid="hero-subheadline"
                    >
                        At a glance, a protocol planner built to replace your
                        spreadsheets and notes.
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.24 }}
                        className="mt-4 max-w-xl text-base lg:text-lg text-white/70 leading-relaxed"
                        data-testid="hero-subheadline-2"
                    >
                        Under the hood, a full hormone optimization platform
                        providing AI-powered bloodwork analysis, advanced
                        pharmacokinetic modeling and a knowledge base
                        covering everything from TRT fundamentals to peptides
                        and advanced enhanced protocols.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.28 }}
                        className="mt-9"
                    >
                        <StoreButtons testIdPrefix="hero" />
                    </motion.div>

                    {/* quick stats */}
                    <div className="mt-12 pt-8 border-t border-white/10 grid grid-cols-3 gap-6 max-w-xl">
                        {[
                            { k: "250+", v: "Studies Referenced" },
                            { k: "100+", v: "Compounds Modeled" },
                            { k: "25yr", v: "Engineering Experience" },
                        ].map((s) => (
                            <div
                                key={s.v}
                                data-testid={`hero-stat-${s.k.replace(/\W/g, "").toLowerCase()}`}
                            >
                                <div className="font-display text-4xl sm:text-5xl font-extrabold text-white leading-none">
                                    {s.k}
                                </div>
                                <div className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-mono text-[#49bef5]">
                                    {s.v}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right column: portrait app card */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.1 }}
                    className="relative mx-auto w-full max-w-[460px] lg:max-w-[520px]"
                    data-testid="hero-card"
                >
                    <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_40px_100px_-20px_rgba(43,166,224,0.35)]">
                        <img
                            src={ASSETS.appCard}
                            alt="TRT+ — optimization for TRT & enhanced athletes"
                            className="w-full h-auto object-cover block"
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                    </div>
                    {/* Floating callout */}
                    <div className="absolute right-0 lg:-right-4 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-2 bg-black/85 border border-white/10 backdrop-blur-xl rounded-full px-3 py-2 text-xs shadow-lg">
                        <span className="pulse-dot-blue w-2 h-2 rounded-full bg-[#2ba6e0]" />
                        <span className="text-white/80 font-mono">
                            PK curve live preview
                        </span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
